/**
 * CSV Export Utility
 * Utility functions for exporting order data to CSV format
 * Handles data formatting, special character escaping, and browser download
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 4 (AC-8)
 */

import type { OrderSearchResultItem } from '../types/filter.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// TYPE DEFINITIONS
// ============================================

/**
 * Order data formatted for CSV export
 */
export interface ExportOrderData {
  orderNumber: string;
  status: OrderStatus;
  amount: string;
  quantity: string;
  orderDate: string;
  deliveryDate: string;
  customerType: string;
  city: string;
  state: string;
}

// ============================================
// CONSTANTS
// ============================================

/**
 * CSV Headers
 */
const CSV_HEADERS = [
  'Order Number',
  'Status',
  'Amount',
  'Quantity',
  'Order Date',
  'Delivery Date',
  'Customer Type',
  'City',
  'State',
] as const;

/**
 * Date formatting constants
 */
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format date to DD/MM/YYYY format
 * @param dateString - ISO date string or null
 * @returns Formatted date string or empty string
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format amount with comma separators (Indian numbering system)
 * @param amount - Number to format
 * @returns Formatted amount string without currency symbol
 */
function formatAmount(amount: number): string {
  // Indian numbering system: XX,XX,XXX
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Escape CSV special characters
 * Handles commas, quotes, and newlines
 * @param value - String value to escape
 * @returns Escaped string safe for CSV
 */
function escapeCsvValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // Replace newlines with spaces
  let escaped = stringValue.replace(/\r?\n|\r/g, ' ');

  // If value contains comma, quote, or newline, wrap in quotes
  if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
    // Escape existing quotes by doubling them
    escaped = escaped.replace(/"/g, '""');
    // Wrap in quotes
    escaped = `"${escaped}"`;
  }

  return escaped;
}

/**
 * Calculate total quantity from order data
 * Note: Currently using totalAmount as proxy since quantity field may not exist
 * TODO: Update when actual quantity field is available in OrderSearchResultItem
 * @param order - Order data
 * @returns Quantity string
 */
function calculateQuantity(order: OrderSearchResultItem): string {
  // Placeholder implementation - update based on actual data structure
  // For now, returning a default value
  return '1';
}

// ============================================
// MAIN EXPORT FUNCTIONS
// ============================================

/**
 * Transform order data to export format
 * @param orders - Array of order search results
 * @returns Array of formatted export data
 */
export function prepareOrdersForExport(
  orders: OrderSearchResultItem[]
): ExportOrderData[] {
  return orders.map((order) => ({
    orderNumber: order.orderNumber,
    status: order.orderStatus,
    amount: formatAmount(order.totalAmount),
    quantity: calculateQuantity(order),
    orderDate: formatDate(order.orderDate),
    deliveryDate: formatDate(order.expectedDeliveryDate),
    customerType: order.customerType,
    city: order.city || '',
    state: order.state || '',
  }));
}

/**
 * Generate CSV content from order data
 * @param orders - Array of order search results
 * @returns CSV content as string
 */
export function generateCSVContent(orders: OrderSearchResultItem[]): string {
  if (!orders || orders.length === 0) {
    return CSV_HEADERS.join(',');
  }

  // Prepare data
  const exportData = prepareOrdersForExport(orders);

  // Build CSV lines
  const csvLines: string[] = [];

  // Add headers
  csvLines.push(CSV_HEADERS.join(','));

  // Add data rows
  exportData.forEach((order) => {
    const row = [
      escapeCsvValue(order.orderNumber),
      escapeCsvValue(order.status),
      escapeCsvValue(order.amount),
      escapeCsvValue(order.quantity),
      escapeCsvValue(order.orderDate),
      escapeCsvValue(order.deliveryDate),
      escapeCsvValue(order.customerType),
      escapeCsvValue(order.city),
      escapeCsvValue(order.state),
    ];

    csvLines.push(row.join(','));
  });

  // Join with newlines
  return csvLines.join('\n');
}

/**
 * Download CSV file in browser
 * Creates a blob and triggers download
 * @param filename - Name of the file to download
 * @param content - CSV content string
 */
export function downloadCSV(filename: string, content: string): void {
  try {
    // Create blob with UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + content], {
      type: 'text/csv;charset=utf-8;',
    });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    // Append to document, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up URL object
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw new Error('Failed to download CSV file');
  }
}

/**
 * Generate filename with timestamp
 * @param prefix - Filename prefix (default: 'orders')
 * @returns Filename with timestamp
 */
export function generateFilename(prefix: string = 'orders'): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${prefix}_${year}-${month}-${day}_${hours}-${minutes}-${seconds}.csv`;
}

/**
 * Export orders to CSV (convenience function)
 * Combines generation and download in one call
 * @param orders - Array of order search results
 * @param filename - Optional custom filename
 */
export function exportOrdersToCSV(
  orders: OrderSearchResultItem[],
  filename?: string
): void {
  const csvContent = generateCSVContent(orders);
  const csvFilename = filename || generateFilename('orders');
  downloadCSV(csvFilename, csvContent);
}
